import CourseCard from 'components/CourseDashboard/CourseCard';
import NoCourses from 'components/CourseDashboard/NoCourses';
import { useTranslation } from 'react-i18next';
import {
    Button,
    ButtonGroup,
    ButtonGroupSelectedVariant,
    Container,
    Icon,
    IconButton,
    Loading,
    Stack,
    styled,
    Tabs,
    Text,
    TextInput,
} from 'shell/components';
import { CourseLobbyLayout } from 'utils/constants';

const Desktop = ({
    courses,
    filteredCourses,
    startCourseSetup,
    searchBarInput,
    onChangeSearchBarInput,
    setIsFilterDrawerOpen,
    selectCourse,
    handleCourseCardDropdown,
    viewArchived,
    setViewArchived,
    courseLobbyLayout,
    setCourseLobbyLayout,
    hasCoursesInView,
}) => {
    const { t } = useTranslation('course');
    const gridLayoutLabel = t('common:controls.layout.grid', 'Grid');
    const listLayoutLabel = t('common:controls.layout.list', 'List');

    if (!courses) return <Loading />;

    return (
        <Container device={'desktop'}>
            <Stack sx={{ width: '100%' }} spacing={4}>
                <Stack spacing={4}>
                    <HeaderStack
                        alignItems={'flex-start'}
                        direction={'row'}
                        justifyContent={'space-between'}
                        spacing={2}
                    >
                        <TitleText component={'h1'} variant={'h6'} weight={'medium'}>
                            {t('course-lobby.title', 'Course Lobby')}
                        </TitleText>
                        <ControlsStack alignItems={'center'} direction={'row'} spacing={4}>
                            <Button
                                color={'neutral'}
                                startIcon={<Icon kind={'filter'} />}
                                variant={'secondary'}
                                onClick={() => setIsFilterDrawerOpen(true)}
                            >
                                {t('common:controls.filters', 'Filters')}
                            </Button>
                            <CustomTextInput
                                disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                                endIcon={
                                    searchBarInput ? (
                                        <IconButton
                                            aria-label={'clear search input'}
                                            kind={'exit'}
                                            variant={'square'}
                                            onClick={() => onChangeSearchBarInput('')}
                                            onMouseDown={(e) => e.preventDefault()}
                                        />
                                    ) : (
                                        <Icon kind={'search'} />
                                    )
                                }
                                placeholder={t('common:controls.search', 'Search')}
                                value={searchBarInput}
                                onChange={(e) => onChangeSearchBarInput(e.target.value)}
                            />
                            <LayoutButtonGroup
                                aria-label={'course lobby layout'}
                                maxWidth={'11rem'}
                                options={[
                                    {
                                        key: 'grid',
                                        value: gridLayoutLabel,
                                        icon: 'taskBoard',
                                        tooltip: gridLayoutLabel,
                                    },
                                    {
                                        key: 'list',
                                        value: listLayoutLabel,
                                        icon: 'listUnordered',
                                        tooltip: listLayoutLabel,
                                    },
                                ]}
                                selected={courseLobbyLayout}
                                selectedVariant={ButtonGroupSelectedVariant.SUBTLE}
                                onChange={(layout) => {
                                    setCourseLobbyLayout(layout);
                                }}
                            />
                            <Button
                                sx={{ whiteSpace: 'nowrap' }}
                                className={'joyride-step-1'}
                                startIcon={<Icon kind={'add'} />}
                                onClick={() => startCourseSetup()}
                            >
                                {t('course-lobby.new-course', 'New Course')}
                            </Button>
                        </ControlsStack>
                    </HeaderStack>
                    <Tabs
                        aria-label={'course lobby tabs'}
                        options={[
                            { key: 'current', value: t('course-lobby.current-courses', 'Current Courses') },
                            { key: 'archived', value: t('course-lobby.archived-courses', 'Archived Courses') },
                        ]}
                        selected={viewArchived ? 'archived' : 'current'}
                        onChange={(key) => setViewArchived(key === 'archived')}
                    />
                </Stack>
                {!hasCoursesInView && <NoCourses type={viewArchived ? 'archived' : 'active'} />}
                {!!filteredCourses.length && (
                    <CoursesStack
                        alignItems={'flex-start'}
                        direction={courseLobbyLayout === CourseLobbyLayout.GRID ? 'row' : 'column'}
                        justifyContent={'flex-start'}
                        layout={courseLobbyLayout}
                        spacing={3}
                    >
                        {filteredCourses.map((course, index) => {
                            return (
                                <CourseCard
                                    key={course.course_id}
                                    className={!index ? 'joyride-step-2' : undefined}
                                    course={course}
                                    handleCourseCardDropdown={handleCourseCardDropdown}
                                    layout={courseLobbyLayout}
                                    onClick={selectCourse}
                                />
                            );
                        })}
                    </CoursesStack>
                )}
            </Stack>
        </Container>
    );
};

const HeaderStack = styled(Stack)(() => {
    return { width: '100%' };
});

const TitleText = styled(Text)(() => {
    return {
        flex: '0 1 auto',
        minWidth: 'min-content',
        wordBreak: 'normal',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        flex: '0 1 auto',
        columnGap: `clamp(${theme.spacing(1)}, 1.5vw, ${theme.spacing(4)})`,
        justifyContent: 'flex-end',
        minWidth: 0,
    };
});

const CoursesStack = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'layout',
})(({ theme, layout }) => {
    return {
        flexWrap: layout === CourseLobbyLayout.GRID ? 'wrap' : 'nowrap',
        marginBottom: theme.spacing(8),
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const LayoutButtonGroup = styled(ButtonGroup)(({ theme }) => {
    return {
        flexShrink: 0,
        height: theme.spacing(9),
        '& button.MuiButton-root': {
            maxHeight: '100%',
            minHeight: theme.spacing(7),
        },
    };
});

export default Desktop;
